// src/components/employee/teamTasks/EmployeeTaskDetailModal.js
import React, { useState, useEffect } from 'react';
import {
    X, Flag, Calendar, Clock, Users, CheckSquare, MessageSquare,
    Paperclip, Activity, AlertTriangle, Upload
} from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import teamTasksService from '../../../services/teamTasks.service';

const EmployeeTaskDetailModal = ({ taskId, onClose, onUpdate }) => {
    const { user } = useAuth();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);

    const tabs = [
        { id: 'overview', label: 'Overview', icon: Activity },
        { id: 'subtasks', label: 'Sub-tasks', icon: CheckSquare, count: task?.subtasks?.length },
        { id: 'comments', label: 'Comments', icon: MessageSquare, count: task?.comments?.length },
        { id: 'attachments', label: 'Attachments', icon: Paperclip, count: task?.attachments?.length }
    ];

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const fetchTaskDetails = async () => {
        try {
            setLoading(true);
            const response = await teamTasksService.getTeamTask(taskId);
            if (response.success) {
                setTask(response.data);
            }
        } catch (error) {
            console.error('Error fetching task details:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTaskDetails();
    }, [taskId]);

    const handleUpdateSubtaskStatus = async (subtaskId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
            const response = await teamTasksService.updateSubTask(taskId, subtaskId, {
                status: newStatus,
                is_completed: newStatus === 'completed'
            });

            if (response.success) {
                fetchTaskDetails();
                onUpdate?.();
            }
        } catch (error) {
            console.error('Error updating subtask:', error);
            alert('Failed to update subtask status');
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        try {
            setSubmittingComment(true);
            const response = await teamTasksService.addComment(taskId, {
                comment_text: newComment,
                mentions: []
            });

            if (response.success) {
                setNewComment('');
                fetchTaskDetails();
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            alert('Failed to add comment');
        } finally {
            setSubmittingComment(false);
        }
    };

    const statusColors = {
        pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        under_review: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        on_hold: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    };

    const priorityColors = {
        immediate: 'text-red-600 dark:text-red-400',
        urgent: 'text-orange-600 dark:text-orange-400',
        next_in_line: 'text-yellow-600 dark:text-yellow-400',
        scheduled: 'text-green-600 dark:text-green-400',
        deferred: 'text-gray-600 dark:text-gray-400'
    };

    const priorityLabels = {
        immediate: '🔴 Immediate',
        urgent: '🟠 Urgent',
        next_in_line: '🟡 Next in Line',
        scheduled: '🟢 Scheduled',
        deferred: '⚪ Deferred'
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                </div>
            </div>
        );
    }

    if (!task) {
        return null;
    }

    const progress = teamTasksService.calculateProgress(task);
    const mySubtasks = task.subtasks?.filter(st => st.assigned_to_employee_id === user?.id) || [];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex-1 pr-4">
                        <div className="flex items-center gap-3 mb-2">
                            <Flag className={`w-6 h-6 ${priorityColors[task.priority]}`} fill="currentColor" />
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {task.title}
                            </h2>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Created by {task.created_by_name} on {formatDate(task.created_at)}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
                                activeTab === tab.id
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            <span>{tab.label}</span>
                            {tab.count > 0 && (
                                <span className={`px-2 py-0.5 rounded-full text-xs ${
                                    activeTab === tab.id ? 'bg-white text-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                                }`}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Status and Progress */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Status
                                    </label>
                                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${statusColors[task.status]}`}>
                                        {task.status.replace('_', ' ').toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Priority
                                    </label>
                                    <span className={`inline-block text-sm font-medium ${priorityColors[task.priority]}`}>
                                        {priorityLabels[task.priority]}
                                    </span>
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Overall Progress
                                    </label>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {progress}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                                    <div
                                        className={`h-3 rounded-full transition-all ${
                                            progress === 100 ? 'bg-green-600' : 'bg-blue-600'
                                        }`}
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Description
                                </label>
                                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                                    {task.description || 'No description provided'}
                                </p>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        <Calendar className="w-4 h-4 inline mr-1" />
                                        Deadline
                                    </label>
                                    <p className="text-gray-900 dark:text-white">{formatDate(task.deadline)}</p>
                                    {task.overdue_days > 0 && (
                                        <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3" />
                                            {task.overdue_days} days overdue
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        <Clock className="w-4 h-4 inline mr-1" />
                                        Estimated Hours
                                    </label>
                                    <p className="text-gray-900 dark:text-white">
                                        {task.estimated_hours ? `${task.estimated_hours} hours` : 'Not set'}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        <Users className="w-4 h-4 inline mr-1" />
                                        Assigned Team
                                    </label>
                                    <p className="text-gray-900 dark:text-white">
                                        {task.assigned_employees_count || 0} members
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Category
                                    </label>
                                    <p className="text-gray-900 dark:text-white">
                                        {task.category || 'Uncategorized'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Sub-tasks Tab */}
                    {activeTab === 'subtasks' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Sub-tasks
                                </h3>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {task.subtasks?.filter(st => st.is_completed).length || 0} / {task.subtasks?.length || 0} completed
                                </span>
                            </div>

                            {mySubtasks.length > 0 && (
                                <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-2 border-purple-200 dark:border-purple-700">
                                    <h4 className="font-semibold text-purple-900 dark:text-purple-400 mb-3 flex items-center gap-2">
                                        <CheckSquare className="w-5 h-5" />
                                        My Assigned Sub-tasks
                                    </h4>
                                    {mySubtasks.map(subtask => (
                                        <div
                                            key={subtask.id}
                                            className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg mb-2 last:mb-0"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={subtask.is_completed}
                                                onChange={() => handleUpdateSubtaskStatus(subtask.id, subtask.status)}
                                                className="w-5 h-5 text-blue-600 rounded mt-0.5 cursor-pointer"
                                            />
                                            <div className="flex-1">
                                                <p className={`text-gray-900 dark:text-white ${
                                                    subtask.is_completed ? 'line-through' : ''
                                                }`}>
                                                    {subtask.title}
                                                </p>
                                                {subtask.description && (
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                        {subtask.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="space-y-2">
                                {task.subtasks && task.subtasks.length > 0 ? (
                                    task.subtasks
                                        .filter(st => st.assigned_to_employee_id !== user?.id)
                                        .map(subtask => (
                                            <div
                                                key={subtask.id}
                                                className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                                            >
                                                <CheckSquare
                                                    className={`w-5 h-5 mt-0.5 ${
                                                        subtask.is_completed ? 'text-green-600' : 'text-gray-400'
                                                    }`}
                                                />
                                                <div className="flex-1">
                                                    <p className={`text-gray-900 dark:text-white ${
                                                        subtask.is_completed ? 'line-through' : ''
                                                    }`}>
                                                        {subtask.title}
                                                    </p>
                                                    {subtask.assigned_to_name && (
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                            Assigned to: {subtask.assigned_to_name}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                ) : (
                                    <p className="text-center text-gray-500 py-8">No sub-tasks yet</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Comments Tab */}
                    {activeTab === 'comments' && (
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Discussion
                            </h3>

                            {/* Comment List */}
                            <div className="space-y-4 mb-6">
                                {task.comments && task.comments.length > 0 ? (
                                    task.comments.map(comment => (
                                        <div key={comment.id} className="flex gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <img
                                                src={comment.employee_avatar || '/default-avatar.png'}
                                                alt={comment.employee_name}
                                                className="w-10 h-10 rounded-full"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-semibold text-gray-900 dark:text-white">
                                                        {comment.employee_name}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {formatDateTime(comment.created_at)}
                                                    </span>
                                                </div>
                                                <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                                                    {comment.comment}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-center text-gray-500 py-8">No comments yet. Start the discussion!</p>
                                )}
                            </div>

                            {/* Add Comment */}
                            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                    rows={3}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                                <div className="flex justify-end mt-2">
                                    <button
                                        onClick={handleAddComment}
                                        disabled={!newComment.trim() || submittingComment}
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {submittingComment ? 'Posting...' : 'Post Comment'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Attachments Tab */}
                    {activeTab === 'attachments' && (
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Attachments
                            </h3>

                            {task.attachments && task.attachments.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {task.attachments.map(attachment => (
                                        <div
                                            key={attachment.id}
                                            className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                                        >
                                            <Paperclip className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 dark:text-white truncate">
                                                    {attachment.file_name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    Uploaded by {attachment.uploaded_by_name} on {formatDate(attachment.created_at)}
                                                </p>
                                            </div>
                                            <a
                                                href={attachment.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                                            >
                                                View
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-gray-500 py-8">No attachments yet</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Read-only view • Contact manager to request changes
                    </p>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmployeeTaskDetailModal;
